// =============================================
// FUEL SYSTEM DATA & SCHEMATIC
// Tank-to-Carburetor fuel delivery system
// =============================================

export const FUEL_COMPONENTS = {
    'fuel-tanks': {
        name: 'Fuel Tanks (Wing / Fuselage)',
        function: 'Store the aircraft\'s fuel supply. Wing tanks are most common in light aircraft, using the wing structure itself as a fuel-tight container (wet wing) or containing a bladder-type tank within the wing. Tank design includes internal baffles to prevent fuel sloshing during maneuvers.',
        location: 'Inside the wings (left and right), or in the fuselage for some designs. Header tanks may sit above the engine to provide gravity feed.',
        failures: 'Leaking seams or bladder deterioration. Contamination from water condensation on tank walls. Blocked internal baffles restricting fuel flow to the outlet. Sumps clogged with sediment.',
        symptoms: 'Visible fuel stains on wing undersurface (leak). Water in fuel samples during pre-flight drain check. Fuel starvation during uncoordinated flight (sloshing issues). Reduced useable fuel capacity.',
        x: 100, y: 80, w: 160, h: 70
    },
    'fuel-caps': {
        name: 'Fuel Tank Caps & Vents',
        function: 'Caps seal the filler openings. Vents allow air to replace fuel as it is consumed, preventing vacuum from forming in the tank which would stop fuel flow. Some caps incorporate the vent; others have separate vent tubes.',
        location: 'Top surface of the wing (filler caps). Vents may be in the cap, at the wing tip, or as a standpipe inside the tank.',
        failures: 'Missing or improperly sealed cap: fuel spillage in flight, especially during maneuvers. Blocked vent: tank cannot breathe, vacuum builds, fuel flow stops (fuel starvation).',
        symptoms: 'Fuel smell in flight (loose cap). Progressive loss of fuel flow with vent blockage — engine runs normally initially, then starves. Collapsed tank bladder (extreme vent blockage).',
        x: 100, y: 30, w: 160, h: 35
    },
    'fuel-sumps': {
        name: 'Sumps & Drain Points',
        function: 'Low points in the fuel system where water and heavy contaminants collect by gravity. Drain valves allow the pilot or mechanic to sample fuel before flight, checking for water (which appears as clear bubbles below the colored fuel) or sediment.',
        location: 'Bottom of each fuel tank, bottom of the gascolator, and sometimes at low points in fuel lines.',
        failures: 'Drain valve stuck open: fuel leak. Drain valve stuck closed or not used: water accumulates and eventually reaches the engine.',
        symptoms: 'Open drain: fuel dripping, fire risk on ground. Water contamination: engine roughness, power loss, possible engine stoppage if significant water reaches carburetor.',
        x: 100, y: 165, w: 160, h: 35
    },
    'fuel-selector': {
        name: 'Fuel Selector Valve',
        function: 'A pilot-operated valve that determines which tank(s) supply fuel to the engine. Typical positions: LEFT, RIGHT, BOTH, and OFF. Allows the pilot to manage fuel balance between tanks and to shut off fuel in an emergency.',
        location: 'Usually in the cockpit floor area, between the tanks and the rest of the fuel system. Connected to the tanks via separate fuel lines.',
        failures: 'Internal seal leak allows cross-feeding between tanks. Selector not fully engaging a detent causes partial fuel flow restriction. Selector stuck — cannot switch tanks.',
        symptoms: 'Unintended fuel imbalance between tanks (cross-feed leak). Fuel starvation if selector between detents and restricting flow. Inability to switch to a tank with fuel if stuck.',
        x: 320, y: 120, w: 120, h: 60
    },
    'fuel-lines': {
        name: 'Fuel Lines & Fittings',
        function: 'Carry fuel from tanks through the system to the engine. Rigid aluminum lines in fixed areas, flexible hose in areas subject to vibration or engine movement. All connections use approved aviation fittings (AN/MS type).',
        location: 'Routed through the wing structure, fuselage, firewall, and engine compartment.',
        failures: 'Chafing against structure wears through lines. Loose fittings leak. Kinked lines restrict flow. Lines near heat sources can cause vapor lock.',
        symptoms: 'Fuel leaks (visible stains, fuel smell). Fuel starvation at higher power if restricted. Vapor lock in hot conditions causing surging or engine stoppage.',
        x: 320, y: 200, w: 120, h: 40
    },
    'gascolator': {
        name: 'Gascolator (Main Fuel Strainer)',
        function: 'A combination strainer and sediment bowl located at a low point in the system, downstream of the fuel selector. It has a fine screen that traps particles and a glass or metal bowl where water and sediment settle for periodic draining.',
        location: 'At the lowest accessible point in the fuel system before the engine-driven pump, typically at the firewall or lower engine area.',
        failures: 'Clogged screen restricts fuel flow. Bowl gasket leak. Drain valve stuck open or seeping. Screen bypass (if damaged) lets contaminants pass to the pump and carburetor.',
        symptoms: 'Clogged: fuel starvation at higher power demands. Leaking: fuel dripping near firewall, serious fire hazard. Contamination bypass: downstream component damage (pump wear, jet blockage).',
        x: 500, y: 120, w: 120, h: 60
    },
    'boost-pump': {
        name: 'Electric Boost Pump',
        function: 'An electrically driven auxiliary pump that provides fuel pressure for engine starting (before the engine-driven pump is running), and as a backup in case the engine-driven pump fails. Also used during takeoff, landing, and when switching tanks to prevent momentary fuel pressure loss.',
        location: 'Often submerged in the fuel tank or installed in the line between the tank and the engine-driven pump.',
        failures: 'Motor failure: pump inoperative. Check valve failure: fuel can flow backward through the pump when off. Wiring fault: intermittent operation.',
        symptoms: 'No fuel pressure for starting if failed and no gravity feed. No backup if engine-driven pump fails in flight. Intermittent fuel pressure fluctuations.',
        x: 320, y: 260, w: 120, h: 50
    },
    'engine-pump': {
        name: 'Engine-Driven Fuel Pump',
        function: 'A mechanical pump driven by the engine (usually a diaphragm type driven by a camshaft eccentric). It provides the primary fuel pressure to deliver fuel from the tanks through the system to the carburetor inlet. It has a built-in bypass so the boost pump can flow through when the engine pump is not running.',
        location: 'Mounted on the engine accessory case.',
        failures: 'Diaphragm rupture: fuel leaks into the engine crankcase or overboard. Valve failure: reduced output pressure. Drive coupling failure: no fuel delivery.',
        symptoms: 'Diaphragm failure: fuel in engine oil, fuel smell, low fuel pressure. Pressure drop: engine lean at high power, fuel pressure gauge low. Total failure: engine fuel starvation (boost pump must be used as backup).',
        x: 500, y: 260, w: 120, h: 50
    },
    'fuel-pressure': {
        name: 'Fuel Pressure Gauge / Flow Indicator',
        function: 'Displays fuel pressure or fuel flow to the pilot. Fuel pressure gauges read the pressure at the carburetor inlet. Some aircraft use fuel flow indicators calibrated in gallons per hour. Both help the pilot detect fuel system problems.',
        location: 'Instrument panel. Sensor located in the fuel line near the carburetor inlet.',
        failures: 'Gauge sticks or reads inaccurately. Sensor line leak. Electrical sender failure (for electric gauges).',
        symptoms: 'Inaccurate gauge: pilot unaware of pressure problems. Line leak: fuel leak near instruments, fire hazard. No reading: loss of fuel system monitoring capability.',
        x: 680, y: 200, w: 120, h: 50
    },
    'carb-inlet': {
        name: 'Carburetor Fuel Inlet',
        function: 'The final connection point where the fuel line from the aircraft system delivers fuel into the carburetor body. Fuel passes through here into the carburetor\'s own inlet strainer, then to the needle valve and float bowl.',
        location: 'On the carburetor body — the physical junction between aircraft fuel system and the engine fuel metering system.',
        failures: 'Fitting leak. Screen blockage at carburetor strainer. Air leaks allowing air into fuel stream.',
        symptoms: 'Fuel leak at carburetor. Reduced fuel flow causing lean running if blocked. Erratic fuel metering if air enters.',
        x: 680, y: 120, w: 120, h: 60
    }
};

export const FUEL_FLOW_PATH = [
    'fuel-tanks', 'fuel-selector', 'gascolator', 'engine-pump', 'fuel-pressure', 'carb-inlet'
];

export const FUEL_ANIMATIONS_DATA = {
    'normal': {
        title: 'Normal Flow — Main Tank',
        description: 'Fuel flows from the selected tank through the selector valve, down through the gascolator (where water and sediment are trapped), into the engine-driven fuel pump, past the fuel pressure sensor, and into the carburetor inlet fitting. From there it enters the float bowl through the needle valve.',
        flowColor: '#c62828',
        highlight: ['fuel-tanks', 'fuel-selector', 'gascolator', 'engine-pump', 'fuel-pressure', 'carb-inlet']
    },
    'switch-tank': {
        title: 'Switching Tanks',
        description: 'When the pilot moves the fuel selector from LEFT to RIGHT (or vice versa), there is a brief transition. The electric boost pump is typically turned ON during the switch to maintain fuel pressure. The flow path changes to draw from the newly selected tank while the previously selected tank feed closes.',
        flowColor: '#ff9800',
        highlight: ['fuel-selector', 'boost-pump']
    },
    'contamination': {
        title: 'Water / Debris Contamination',
        description: 'Water is denser than aviation fuel and settles to the lowest points: tank sumps, gascolator bowl, and low spots in lines. During pre-flight, draining these sumps reveals any water (clear bubbles below dyed fuel). If water reaches the carburetor, it disrupts combustion — engine roughness, power loss, or stoppage.',
        flowColor: '#42a5f5',
        highlight: ['fuel-sumps', 'gascolator', 'fuel-tanks']
    },
    'vapor-lock': {
        title: 'Vapor Lock',
        description: 'When fuel lines are exposed to excessive heat (near exhaust pipes, hot engine compartment), fuel can boil and form vapor bubbles. These bubbles block the fuel flow in lines or displace liquid fuel from the pump, starving the engine. Symptoms include surging, power loss, or complete engine stoppage. The boost pump can sometimes push through a vapor lock by increasing pressure in the line.',
        flowColor: '#ffeb3b',
        highlight: ['fuel-lines', 'engine-pump', 'boost-pump']
    }
};

// Draw the fuel system schematic on a canvas
export function drawFuelSchematic(canvas, activeAnim = 'normal', selectedComponent = null, time = 0) {
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;

    ctx.clearRect(0, 0, w, h);

    // Background
    ctx.fillStyle = '#0d1117';
    ctx.fillRect(0, 0, w, h);

    // Title
    ctx.fillStyle = '#94a3b8';
    ctx.font = '11px Inter, sans-serif';
    ctx.fillText('FUEL STORAGE & DELIVERY — TANK TO CARBURETOR', 20, 20);

    const animData = FUEL_ANIMATIONS_DATA[activeAnim];
    const highlights = animData ? animData.highlight : [];

    // Draw connections (pipes)
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 3;

    const connections = [
        ['fuel-tanks', 'fuel-selector'],
        ['fuel-selector', 'gascolator'],
        ['fuel-selector', 'fuel-lines'],
        ['fuel-lines', 'gascolator'],
        ['gascolator', 'engine-pump'],
        ['boost-pump', 'engine-pump'],
        ['engine-pump', 'fuel-pressure'],
        ['fuel-pressure', 'carb-inlet'],
    ];

    connections.forEach(([from, to]) => {
        const a = FUEL_COMPONENTS[from];
        const b = FUEL_COMPONENTS[to];
        if (!a || !b) return;

        const ax = a.x + a.w / 2;
        const ay = a.y + a.h / 2;
        const bx = b.x + b.w / 2;
        const by = b.y + b.h / 2;

        const isHighlighted = highlights.includes(from) && highlights.includes(to);
        ctx.strokeStyle = isHighlighted ? (animData?.flowColor || '#c62828') : '#2a3040';
        ctx.lineWidth = isHighlighted ? 4 : 2;

        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
        ctx.stroke();

        // Animated flow dots
        if (isHighlighted) {
            const dots = 5;
            for (let i = 0; i < dots; i++) {
                const t = ((time / 2000 + i / dots) % 1);
                const dx = ax + (bx - ax) * t;
                const dy = ay + (by - ay) * t;
                ctx.beginPath();
                ctx.arc(dx, dy, 4, 0, Math.PI * 2);
                ctx.fillStyle = animData?.flowColor || '#c62828';
                ctx.fill();
            }
        }
    });

    // Draw components
    Object.entries(FUEL_COMPONENTS).forEach(([id, comp]) => {
        const isSelected = selectedComponent === id;
        const isHighlighted = highlights.includes(id);

        // Box
        ctx.fillStyle = isSelected ? 'rgba(79,195,247,.2)' : isHighlighted ? 'rgba(198,40,40,.15)' : 'rgba(26,34,54,.8)';
        ctx.strokeStyle = isSelected ? '#4fc3f7' : isHighlighted ? '#c62828' : '#333';
        ctx.lineWidth = isSelected ? 2 : 1;

        roundRect(ctx, comp.x, comp.y, comp.w, comp.h, 6);
        ctx.fill();
        ctx.stroke();

        // Label
        ctx.fillStyle = isSelected ? '#4fc3f7' : isHighlighted ? '#ef5350' : '#e2e8f0';
        ctx.font = `${isSelected ? 'bold ' : ''}11px Inter, sans-serif`;

        // Word wrap the name
        const words = comp.name.split(' ');
        let lines = [];
        let currentLine = '';
        words.forEach(word => {
            const testLine = currentLine ? currentLine + ' ' + word : word;
            if (ctx.measureText(testLine).width > comp.w - 12) {
                lines.push(currentLine);
                currentLine = word;
            } else {
                currentLine = testLine;
            }
        });
        if (currentLine) lines.push(currentLine);

        const lineHeight = 13;
        const startY = comp.y + (comp.h - lines.length * lineHeight) / 2 + 10;
        lines.forEach((line, i) => {
            ctx.fillText(line, comp.x + 6, startY + i * lineHeight);
        });
    });

    // Arrow to carburetor
    ctx.strokeStyle = '#c62828';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 3]);
    ctx.beginPath();
    ctx.moveTo(800, 150);
    ctx.lineTo(860, 150);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = '#c62828';
    ctx.beginPath();
    ctx.moveTo(860, 145);
    ctx.lineTo(870, 150);
    ctx.lineTo(860, 155);
    ctx.fill();

    ctx.fillStyle = '#ef5350';
    ctx.font = 'bold 12px Inter, sans-serif';
    ctx.fillText('→ To Float Bowl', 810, 105);

    // Animation info
    if (animData) {
        ctx.fillStyle = 'rgba(17,24,39,.85)';
        roundRect(ctx, 20, h - 90, w - 40, 75, 8);
        ctx.fill();

        ctx.fillStyle = animData.flowColor || '#94a3b8';
        ctx.font = 'bold 12px Inter, sans-serif';
        ctx.fillText(animData.title, 30, h - 68);

        ctx.fillStyle = '#94a3b8';
        ctx.font = '11px Inter, sans-serif';
        wrapText(ctx, animData.description, 30, h - 52, w - 60, 14);
    }
}

function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}

function wrapText(ctx, text, x, y, maxW, lineH) {
    const words = text.split(' ');
    let line = '';
    let cy = y;
    words.forEach(word => {
        const test = line + word + ' ';
        if (ctx.measureText(test).width > maxW) {
            ctx.fillText(line, x, cy);
            line = word + ' ';
            cy += lineH;
        } else {
            line = test;
        }
    });
    ctx.fillText(line, x, cy);
}

export function getFuelComponentAt(canvas, x, y) {
    const rect = canvas.getBoundingClientRect();
    const sx = (x - rect.left) * (canvas.width / rect.width);
    const sy = (y - rect.top) * (canvas.height / rect.height);

    for (const [id, comp] of Object.entries(FUEL_COMPONENTS)) {
        if (sx >= comp.x && sx <= comp.x + comp.w && sy >= comp.y && sy <= comp.y + comp.h) {
            return id;
        }
    }
    return null;
}
